import { Component, inject, input, signal, computed } from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { GridIconComponent } from '@components/grid-icon/grid-icon.component';
import { GridTooltipComponent } from '@components/grid-tooltip/grid-tooltip.component';
import { GroupCellRendererComponent } from '@components/group-cell-renderer/group-cell-renderer.component';
import { dateFormattingOptions } from '@domain/formatting';
import { AuditTrailItem, LocaleValue } from '@hjc/masterdata-types';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, themeAlpine } from 'ag-grid-community';
import { isArray, isDate, isEmpty, isObject } from 'lodash-es';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedTranslationModule } from '@shared/translation.module';
import { LocalizationService } from '@services/localization.service';

@Component({
  selector: 'md-audit-trail',
  imports: [AgGridAngular, MatDivider, TranslateModule, SharedTranslationModule],
  templateUrl: './audit-trail.component.html',
  styleUrl: './audit-trail.component.scss',
  standalone: true
})
export class AuditTrailComponent {
  translate = inject(TranslateService);
  localizationService = inject(LocalizationService);

  auditTrail = input<AuditTrailItem[]>([]);

  private gridApi!: GridApi<AuditTrailItem>;

  theme = themeAlpine;
  lang = signal<LocaleValue>('fi');
  // Signal to trigger column def updates when translations change
  translationsLoaded = signal<boolean>(false);

  // Use computed for reactive column definitions
  columnDefs = computed(() => {
    // Include translationsLoaded signal to trigger recomputation
    this.translationsLoaded();
    return this.getColumnDefs();
  });

  constructor() {
    // Initialize language from service
    this.lang.set(this.localizationService.currentLang);

    // Listen for translation changes to trigger column def updates
    this.translate.onTranslationChange.subscribe(() => {
      this.translationsLoaded.set(!this.translationsLoaded());
    });

    // Also listen for default language changes
    this.translate.onDefaultLangChange.subscribe(() => {
      this.translationsLoaded.set(!this.translationsLoaded());
    });

    // Initial trigger when translations are already loaded
    if (this.translate.currentLang) {
      this.translationsLoaded.set(true);
    }
  }

  tooltipShowDelay = 0;
  tooltipHideDelay = 5000;

  defaultColDef: ColDef = {
    autoHeight: true,
    flex: 1
  };

  private getColumnDefs(): ColDef<AuditTrailItem>[] {
    return [
      { headerName: this.translate.instant('AUDIT_TRAIL.EVENT'), field: 'operation', sortable: false },
      {
        headerName: this.translate.instant('AUDIT_TRAIL.CHANGED_FIELDS'),
        valueGetter: p => {
          if (p.data?.diff) {
            return Object.keys(p.data?.diff);
          }
          return;
        },
        cellRendererSelector: params => {
          return {
            component: GridIconComponent,
            params: { value: params.value, icon: 'info' }
          };
        },
        tooltipComponent: GridTooltipComponent,
        tooltipValueGetter: p => {
          if (p.data?.diff) {
            return {
              diff: this.processNestedDiff(p.data?.diff)
            };
          }
          return;
        },
        cellRenderer: GroupCellRendererComponent,
        autoHeight: true,
        sortable: false
      },
      { headerName: this.translate.instant('AUDIT_TRAIL.USER'), field: 'updatedBy', sortable: false, filter: true },
      {
        headerName: this.translate.instant('AUDIT_TRAIL.TIMESTAMP'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions)
      }
    ];
  }

  /**
   * Processes a diff object to extract all changed values
   * @param diff The diff object containing changes
   * @returns Array of flattened diff objects
   */
  processNestedDiff(diff: Record<string, unknown>): DiffChange[] {
    // Empty or invalid diff
    if (!diff || !isObject(diff) || isEmpty(diff)) {
      return [];
    }

    const changes: DiffChange[] = [];

    // Process INSERT operations (keys ending with __added)
    const addedKeys = Object.keys(diff).filter(key => key.endsWith('__added'));
    if (addedKeys.length > 0) {
      // This is an INSERT operation
      addedKeys.forEach(key => {
        const fieldName = key.replace('__added', '');
        const value = diff[key];

        // If the value is an object, handle it specially
        if (isObject(value) && !isArray(value)) {
          this.processAddedObject(fieldName, value as Record<string, unknown>, changes);
        } else {
          changes.push({
            path: fieldName,
            __old: 'undefined',
            __new: this.formatValue(value)
          });
        }
      });
    } else {
      // Process MODIFY operations (objects with __old and __new)
      this.extractChanges(diff, '', changes);
    }

    return changes;
  }

  /**
   * Processes an object that was added in an INSERT operation
   */
  private processAddedObject(fieldName: string, obj: Record<string, unknown>, result: DiffChange[]): void {
    Object.entries(obj).forEach(([key, value]) => {
      const nestedPath = `${fieldName}.${key}`;

      if (isObject(value) && !isArray(value)) {
        this.processAddedObject(nestedPath, value as Record<string, unknown>, result);
      } else {
        result.push({
          path: nestedPath,
          __old: 'undefined',
          __new: this.formatValue(value)
        });
      }
    });
  }

  /**
   * Recursively extracts changes from a diff object for MODIFY operations
   */
  private extractChanges(obj: Record<string, unknown>, path: string, result: DiffChange[]): void {
    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key;

      if (isObject(value)) {
        if ('__old' in value && '__new' in value) {
          // This is a direct change
          const change = value as { __old: unknown; __new: unknown };
          result.push({
            path: currentPath,
            __old: this.formatValue(change.__old),
            __new: this.formatValue(change.__new)
          });
        } else {
          // This is a nested object, continue recursion
          this.extractChanges(value as Record<string, unknown>, currentPath, result);
        }
      }
    });
  }

  /**
   * Formats a value for display in the tooltip
   */
  private formatValue(value: unknown): string {
    if (value === null || value === undefined) {
      return String(value);
    }

    if (isDate(value)) {
      // Handle dates
      const date = isDate(value) ? (value as Date) : new Date(value as string);
      return date.toLocaleString(this.lang(), dateFormattingOptions);
    }

    return String(value);
  }

  onRowDataUpdated(): void {
    if (this.auditTrail().some(item => item.type === 'pricing' || item.type === 'rate')) {
      this.gridApi.setGridOption('columnDefs', [
        ...this.columnDefs(),
        {
          headerName: this.translate.instant('AUDIT_TRAIL.TYPE'),
          field: 'type',
          filter: true
        }
      ]);
    }
  }

  onGridReady(params: GridReadyEvent<AuditTrailItem>) {
    this.gridApi = params.api;
  }
}

type DiffChange = {
  path: string;
  __old: unknown;
  __new: unknown;
};
